(() => {
  'use strict';

  const root = document.querySelector('[data-jsk-sabbath-closure]');
  if (!root) return;

  const timeZone = root.dataset.timeZone || 'America/New_York';
  const latitude = Number(root.dataset.latitude || 40.7357);
  const longitude = Number(root.dataset.longitude || -74.1724);
  const reopenNode = root.querySelector('[data-jsk-sabbath-reopen]');
  const panel = root.querySelector('[role="dialog"]');
  const dayMs = 86400000;
  let closed = false;

  const radians = (degrees) => (degrees * Math.PI) / 180;
  const degrees = (radiansValue) => (radiansValue * 180) / Math.PI;
  const normalize = (value, maximum) => ((value % maximum) + maximum) % maximum;

  function calendarParts(date) {
    const values = {};
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });

    formatter.formatToParts(date).forEach((part) => {
      if (part.type !== 'literal') values[part.type] = part.value;
    });

    return {
      weekday: values.weekday,
      year: Number(values.year),
      month: Number(values.month),
      day: Number(values.day),
    };
  }

  function addCalendarDays(parts, amount) {
    const shifted = new Date(Date.UTC(parts.year, parts.month - 1, parts.day) + amount * dayMs);
    return {
      year: shifted.getUTCFullYear(),
      month: shifted.getUTCMonth() + 1,
      day: shifted.getUTCDate(),
    };
  }

  function nextSaturday(parts) {
    const weekdayOrder = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const currentIndex = weekdayOrder[parts.weekday];
    const daysAhead = currentIndex === undefined ? 0 : (6 - currentIndex + 7) % 7;
    return addCalendarDays(parts, daysAhead);
  }

  function sunsetUtc(parts) {
    const yearStart = Date.UTC(parts.year, 0, 0);
    const currentDay = Date.UTC(parts.year, parts.month - 1, parts.day);
    const dayOfYear = Math.floor((currentDay - yearStart) / dayMs);
    const longitudeHour = longitude / 15;
    const approximateTime = dayOfYear + (18 - longitudeHour) / 24;
    const meanAnomaly = 0.9856 * approximateTime - 3.289;
    const trueLongitude = normalize(
      meanAnomaly +
        1.916 * Math.sin(radians(meanAnomaly)) +
        0.02 * Math.sin(radians(2 * meanAnomaly)) +
        282.634,
      360
    );

    let rightAscension = normalize(degrees(Math.atan(0.91764 * Math.tan(radians(trueLongitude)))), 360);
    const longitudeQuadrant = Math.floor(trueLongitude / 90) * 90;
    const ascensionQuadrant = Math.floor(rightAscension / 90) * 90;
    rightAscension = (rightAscension + longitudeQuadrant - ascensionQuadrant) / 15;

    const sinDeclination = 0.39782 * Math.sin(radians(trueLongitude));
    const cosDeclination = Math.cos(Math.asin(sinDeclination));
    const cosHourAngle =
      (Math.cos(radians(90.833)) - sinDeclination * Math.sin(radians(latitude))) /
      (cosDeclination * Math.cos(radians(latitude)));

    if (cosHourAngle < -1 || cosHourAngle > 1) return null;

    const hourAngle = degrees(Math.acos(cosHourAngle)) / 15;
    const localMeanTime = hourAngle + rightAscension - 0.06571 * approximateTime - 6.622;
    const universalTime = normalize(localMeanTime - longitudeHour, 24);

    let sunset = new Date(currentDay + universalTime * 3600000);
    const targetDate = parts.year * 10000 + parts.month * 100 + parts.day;
    let localDate = calendarParts(sunset);
    let renderedDate = localDate.year * 10000 + localDate.month * 100 + localDate.day;

    // In New Jersey, late spring and summer sunsets occur after midnight UTC.
    // NOAA's normalized UTC hour omits that date rollover, so align the result
    // back to the requested calendar date in the store's configured timezone.
    if (renderedDate < targetDate) sunset = new Date(sunset.getTime() + dayMs);
    if (renderedDate > targetDate) sunset = new Date(sunset.getTime() - dayMs);

    return sunset;
  }

  function schedule(now = new Date()) {
    const local = calendarParts(now);
    let friday;
    let saturday;

    if (local.weekday === 'Fri') {
      friday = local;
      saturday = addCalendarDays(local, 1);
    } else if (local.weekday === 'Sat') {
      friday = addCalendarDays(local, -1);
      saturday = local;
    } else {
      return { isClosed: false, reopenAt: null };
    }

    const closesAt = sunsetUtc(friday);
    const reopensAt = sunsetUtc(saturday);
    if (!closesAt || !reopensAt) return { isClosed: false, reopenAt: null };

    return {
      isClosed: now >= closesAt && now < reopensAt,
      reopenAt: reopensAt,
    };
  }

  function isPreviewOverride() {
    const params = new URLSearchParams(window.location.search);
    return params.get('jsk_sabbath_preview') === 'closed';
  }

  function formatReopening(date) {
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(date);
  }

  function setPageInert(value) {
    document.querySelectorAll('body > *:not(#JskSabbathClosure):not(script):not(style)').forEach((node) => {
      if (value) {
        node.setAttribute('inert', '');
        node.setAttribute('data-jsk-sabbath-inert', 'true');
        node.setAttribute('aria-hidden', 'true');
      } else if (node.hasAttribute('data-jsk-sabbath-inert')) {
        node.removeAttribute('inert');
        node.removeAttribute('data-jsk-sabbath-inert');
        node.removeAttribute('aria-hidden');
      }
    });
  }

  function blockCommerce(event) {
    if (!closed) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const commerceTarget = target.closest(
      'form[action*="/cart/add"], [name="checkout"], a[href*="/checkout"], .shopify-payment-button, button[type="submit"][name="add"]'
    );
    if (!commerceTarget) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    panel?.focus({ preventScroll: true });
  }

  function applyState() {
    const timing = schedule();
    const previewClosed = isPreviewOverride();
    const shouldClose = timing.isClosed || previewClosed;

    if (shouldClose === closed && root.hidden === !shouldClose) return;
    closed = shouldClose;
    root.hidden = !closed;
    document.body.classList.toggle('jsk-sabbath-closed', closed);
    setPageInert(closed);

    if (closed) {
      const reopenAt = timing.reopenAt || sunsetUtc(nextSaturday(calendarParts(new Date())));
      if (reopenNode && reopenAt) {
        reopenNode.dateTime = reopenAt.toISOString();
        reopenNode.textContent = formatReopening(reopenAt);
      }
      panel?.focus({ preventScroll: true });
      window.requestAnimationFrame(() => panel?.focus({ preventScroll: true }));
    }
  }

  document.addEventListener('click', blockCommerce, true);
  document.addEventListener('submit', blockCommerce, true);
  document.addEventListener('keydown', (event) => {
    if (closed && event.key === 'Escape') event.preventDefault();
  });

  applyState();
  window.setInterval(applyState, 60000);
  window.JSKSabbathClosure = Object.freeze({
    getState: (at) => schedule(at ? new Date(at) : new Date()),
    refresh: applyState,
  });
})();
